import * as m from '@staiter/i18n';
import type { VisibilityAutoAddReason } from '@staiter/shared/task-module';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button, Checkbox, Dialog } from '~/components/primitives';
import { fetchVisibilityPreview } from '~/lib/api/projects';

/**
 * project-visibility (F3) — диалог перехода видимости.
 *   open→private: member-review (число участников, «вся компания», чек-лист
 *     авто-добавляемых со снятием галки, число ролевых очередей).
 *   private→open: простое подтверждение «станет видно всей компании».
 * Числа берутся из BE preview (B20) — FE не считает сам (инвариант 4).
 */

const REASON_LABEL: Record<VisibilityAutoAddReason, () => string> = {
  task_assignee: () => m.tm_visibility_reason_task_assignee(),
  claimed_assignee: () => m.tm_visibility_reason_claimed_assignee(),
  draft_author: () => m.tm_visibility_reason_draft_author(),
  project_owner: () => m.tm_visibility_reason_project_owner(),
};

export function VisibilityChangeDialog({
  open,
  projectId,
  target,
  onCancel,
  onConfirm,
  pending,
}: {
  open: boolean;
  projectId: string;
  target: 'open' | 'private';
  onCancel: () => void;
  onConfirm: (excludeUserIds: string[]) => void;
  pending: boolean;
}): JSX.Element | null {
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (open) setExcluded(new Set());
  }, [open]);

  const previewQuery = useQuery({
    queryKey: ['project', projectId, 'visibility-preview'],
    queryFn: () => fetchVisibilityPreview(projectId),
    enabled: open && target === 'private',
  });
  const preview = previewQuery.data;
  const wholeCompany =
    preview !== undefined &&
    preview.company_active_user_count > 0 &&
    preview.member_count + preview.auto_add_candidates.length >= preview.company_active_user_count;

  if (!open) return null;

  const toggle = (uid: string) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });

  const title =
    target === 'private'
      ? m.tm_visibility_dialog_private_title()
      : m.tm_visibility_dialog_open_title();

  return (
    <Dialog
      open
      onClose={onCancel}
      title={title}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            {m.common_button_cancel()}
          </Button>
          <Button
            variant={target === 'open' ? 'destructive' : 'primary'}
            disabled={pending || (target === 'private' && previewQuery.isLoading)}
            onClick={() => onConfirm([...excluded])}
          >
            {m.tm_visibility_confirm()}
          </Button>
        </>
      }
    >
      {target === 'open' ? (
        <p style={{ color: 'var(--text-danger, var(--danger))' }}>
          {m.tm_visibility_open_confirm_body()}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {previewQuery.isLoading && <p>…</p>}
          {preview && (
            <>
              <p style={{ color: 'var(--text-secondary)' }}>
                {m.tm_visibility_members_line({ count: preview.member_count })}
              </p>
              {wholeCompany && (
                <p
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    background: 'color-mix(in oklch, var(--warn) 12%, transparent)',
                    color: 'var(--text-primary)',
                    fontSize: 'var(--text-sm)',
                  }}
                >
                  {m.tm_visibility_whole_company_warn()}
                </p>
              )}
              {preview.auto_add_candidates.length > 0 && (
                <div>
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                    {m.tm_visibility_auto_add_title()}
                  </p>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {preview.auto_add_candidates.map((cnd) => (
                      <li
                        key={cnd.user_id}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}
                      >
                        <Checkbox
                          checked={!excluded.has(cnd.user_id)}
                          onChange={() => toggle(cnd.user_id)}
                          label={`${cnd.full_name ?? cnd.email ?? cnd.user_id} · ${REASON_LABEL[cnd.reason]()}`}
                        />
                      </li>
                    ))}
                  </ul>
                  {excluded.size > 0 && (
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--warn)' }}>
                      {m.tm_visibility_unchecked_warn()}
                    </p>
                  )}
                </div>
              )}
              {preview.role_queue_count > 0 && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                  {m.tm_visibility_role_queues_warn({ count: preview.role_queue_count })}
                </p>
              )}
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {m.tm_visibility_authors_note()}
              </p>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}
